import { useState, useEffect, useCallback } from 'react';
import { http } from '../lib/http';
import { useSocket } from '../contexts/SocketContext';

export interface DispatchMode {
  mode: 'active' | 'self';
  hasActiveShift: boolean;
  dispatchStatus: string | null;
  isDispatchInService: boolean;
  activeShift: any | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to detect and track the current dispatch mode
 * 
 * - Active Dispatch: Dispatcher is I/S OR there is an active shift
 * - Self Dispatch: Dispatcher is OOS AND no active shift
 */
export const useDispatchMode = () => {
  const [modeInfo, setModeInfo] = useState<DispatchMode>({
    mode: 'self',
    hasActiveShift: false,
    dispatchStatus: null,
    isDispatchInService: false,
    activeShift: null,
    isLoading: true,
    error: null
  });

  const socket = useSocket();

  // Fetch current dispatch mode
  const fetchDispatchMode = useCallback(async () => {
    try {
      const response = await http.get('/auth/dispatch-mode');
      
      if (response.data.success) {
        setModeInfo({
          ...response.data.data,
          isLoading: false,
          error: null
        });
      } else {
        setModeInfo(prev => ({
          ...prev,
          isLoading: false,
          error: response.data.error || 'Failed to fetch dispatch mode'
        }));
      }
    } catch (error: any) {
      console.error('[useDispatchMode] Error fetching dispatch mode:', error);
      setModeInfo(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch dispatch mode'
      }));
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchDispatchMode();
  }, [fetchDispatchMode]);

  // Listen for mode changes via socket events
  useEffect(() => {
    if (!socket) return;

    // Refetch mode when shift changes
    const handleShiftCreated = () => {
      console.log('[useDispatchMode] Shift created, refetching mode');
      fetchDispatchMode();
    };

    const handleShiftUpdated = () => {
      console.log('[useDispatchMode] Shift updated, refetching mode');
      fetchDispatchMode();
    };

    const handleShiftDeleted = () => {
      console.log('[useDispatchMode] Shift deleted, refetching mode');
      fetchDispatchMode();
    };

    const handleShiftActivated = () => {
      console.log('[useDispatchMode] Shift activated, refetching mode');
      fetchDispatchMode();
    };

    // Refetch mode when DISPATCH unit status changes
    const handleUnitStatusChanged = (data: any) => {
      if (data.unitId === 'DISPATCH') {
        console.log('[useDispatchMode] DISPATCH unit status changed, refetching mode');
        fetchDispatchMode();
      }
    };

    socket.on('shift-created', handleShiftCreated);
    socket.on('shift-updated', handleShiftUpdated);
    socket.on('shift-deleted', handleShiftDeleted);
    socket.on('shift-activated', handleShiftActivated);
    socket.on('unit-status-changed', handleUnitStatusChanged);

    return () => {
      socket.off('shift-created', handleShiftCreated);
      socket.off('shift-updated', handleShiftUpdated);
      socket.off('shift-deleted', handleShiftDeleted);
      socket.off('shift-activated', handleShiftActivated);
      socket.off('unit-status-changed', handleUnitStatusChanged);
    };
  }, [socket, fetchDispatchMode]);

  return {
    ...modeInfo,
    refresh: fetchDispatchMode,
    isActiveDispatchMode: modeInfo.mode === 'active',
    isSelfDispatchMode: modeInfo.mode === 'self'
  };
};

